/** Aligné sur backend/src/academia/categorieParticipant.ts */

export const CATEGORIE_PARTICIPANT = {
  JEUNESSE_PRIORITAIRE: 'JEUNESSE_PRIORITAIRE',
  ADULTE_STANDARD: 'ADULTE_STANDARD',
  FONCTIONNAIRE_EMPLOYE: 'FONCTIONNAIRE_EMPLOYE',
};

export const CATEGORIE_LABELS = {
  JEUNESSE_PRIORITAIRE: 'Jeunesse prioritaire (17–29 ans)',
  ADULTE_STANDARD: 'Adulte standard (30 ans et +)',
  FONCTIONNAIRE_EMPLOYE: 'Fonctionnaire / employé',
};

/**
 * @param {string} dateNaissance ISO date (yyyy-mm-dd)
 * @param {Date} [at]
 * @returns {'JEUNESSE_PRIORITAIRE'|'ADULTE_STANDARD'|null}
 */
function parseLocalBirthDate(dateNaissance) {
  const match = String(dateNaissance || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const birth = new Date(year, month - 1, day);
  if (
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return null;
  }
  return birth;
}

export function isValidBirthDateInput(dateNaissance) {
  return parseLocalBirthDate(dateNaissance) !== null;
}

export function resolveCategorieFromBirthDate(dateNaissance, at = new Date()) {
  const birth = parseLocalBirthDate(dateNaissance);
  if (!birth) return null;

  let age = at.getFullYear() - birth.getFullYear();
  const m = at.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < birth.getDate())) {
    age -= 1;
  }

  if (age >= 17 && age <= 29) {
    return CATEGORIE_PARTICIPANT.JEUNESSE_PRIORITAIRE;
  }
  if (age >= 30) {
    return CATEGORIE_PARTICIPANT.ADULTE_STANDARD;
  }
  return null;
}

/**
 * @param {{ dateNaissance?: string, isFonctionnaire?: boolean }} params
 * @returns {'JEUNESSE_PRIORITAIRE'|'ADULTE_STANDARD'|'FONCTIONNAIRE_EMPLOYE'|null}
 */
export function resolveCategorieParticipant({ dateNaissance = '', isFonctionnaire = false } = {}) {
  if (isFonctionnaire) {
    return CATEGORIE_PARTICIPANT.FONCTIONNAIRE_EMPLOYE;
  }
  return resolveCategorieFromBirthDate(dateNaissance);
}

export function getCategorieLabel(code) {
  if (!code) return null;
  return CATEGORIE_LABELS[code] ?? code;
}

/**
 * @param {string} dateNaissance
 * @param {Date} [at]
 * @returns {number|null}
 */
export function computeAgeFromBirthDate(dateNaissance, at = new Date()) {
  const birth = parseLocalBirthDate(dateNaissance);
  if (!birth) return null;

  let age = at.getFullYear() - birth.getFullYear();
  const m = at.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

/**
 * Infos catégorie calculées côté client (repli si l'API ne les fournit pas).
 * @param {{ dateNaissance?: string|null, categorie?: string|null, profession?: string|null, age?: number|null, categorisationRaison?: string|null }} input
 */
export function resolveIdentityCategorisation(input = {}) {
  const dateNaissance = input.dateNaissance ?? '';
  const age =
    input.age ?? (dateNaissance ? computeAgeFromBirthDate(dateNaissance) : null);
  let categorie = input.categorie ?? null;
  let categorisationRaison = input.categorisationRaison ?? null;

  if (!categorie && dateNaissance) {
    categorie = resolveCategorieFromBirthDate(dateNaissance);
  }

  if (!categorisationRaison && categorie && age != null) {
    if (categorie === CATEGORIE_PARTICIPANT.JEUNESSE_PRIORITAIRE) {
      categorisationRaison = `Âge ${age} ans (17-29) => Jeunesse prioritaire`;
    } else if (categorie === CATEGORIE_PARTICIPANT.ADULTE_STANDARD) {
      categorisationRaison = `Âge ${age} ans (>=30) => Adulte standard`;
    } else if (categorie === CATEGORIE_PARTICIPANT.FONCTIONNAIRE_EMPLOYE) {
      categorisationRaison = `Profil socio-professionnel (${input.profession?.trim() || 'profession non renseignée'})`;
    }
  }

  return { age, categorie, categorisationRaison };
}
